import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Pencil, Trash2, Check, Undo2, Briefcase, ChevronRight, UserRound, Home, Wrench, Receipt, Plus, Bell } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Spinner } from "./ui/kit";
import OsobaKolecko from "./OsobaKolecko";
import NovaAktivita from "./NovaAktivita";
import Komentare from "./Komentare";
import { Button } from "./ui/kit";
import { ikonaTypu, textUpozorneni } from "../lib/typyAktivit";

/**
 * Detail aktivity — overlay (desktop) / bottom sheet (mobil). Varianta 11A
 * ze schváleného plátna („jako platba v Revolutu"): ikona typu uprostřed,
 * název a termín, jedno velké tlačítko Hotovo, pod tím údaje jako řádky
 * s prokliknutím. Hotovou aktivitu nepřeškrtáváme — pozná se podle stavu.
 */

const DNY = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

/** „Čt 2. 7. 08:30 – 09:30" / „Čt 2. 7. – Pá 3. 7. 2026 · celý den" */
function formatRozsah(from, to) {
  if (!from) return "";
  const s = new Date(from);
  const e = to ? new Date(to) : null;
  const den = (d) => `${DNY[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`;
  const cas = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (!e) return `${den(s)} ${cas(s)}`;
  const stejnyDen = s.toDateString() === e.toDateString();
  // celý den = od 0:00 do 23:59 nebo přes celé dny
  const celyDen = s.getHours() === 0 && s.getMinutes() === 0 &&
    ((e.getHours() === 23 && e.getMinutes() >= 59) || (e.getHours() === 0 && e.getMinutes() === 0 && !stejnyDen));
  if (celyDen) {
    return stejnyDen
      ? `${den(s)} ${s.getFullYear()} · celý den`
      : `${den(s)} – ${den(e)} ${e.getFullYear()} · celý den`;
  }
  return stejnyDen
    ? `${den(s)} ${s.getFullYear()} · ${cas(s)} – ${cas(e)}`
    : `${den(s)} ${cas(s)} – ${den(e)} ${cas(e)}`;
}

function Radek({ ikona: Ikona, label, children, onClick, testid }) {
  const Obal = onClick ? "button" : "div";
  return (
    <Obal onClick={onClick} data-testid={testid}
      className={`w-full min-h-[52px] px-3 py-1.5 flex items-center gap-3 rounded-[12px] text-left ${onClick ? "hover:bg-white/70 active:scale-[.99] transition" : ""}`}>
      <span className="h-[34px] w-[34px] rounded-[10px] bg-white text-sec inline-flex items-center justify-center shrink-0">
        <Ikona size={17} strokeWidth={1.9} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11.5px] text-muted">{label}</span>
        <span className="block t-telo font-600 text-ink">{children}</span>
      </span>
      {onClick && <ChevronRight size={16} className="text-muted-2 shrink-0" />}
    </Obal>
  );
}

export default function AktivitaDetail({ id, onClose, onEdit, onChanged }) {
  const nav = useNavigate();
  const [a, setA] = useState(undefined);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/aktivity/${id}`);
        if (alive) setA(data);
      } catch (e) {
        if (alive) { setA(null); setErr(apiError(e.response?.data?.detail)); }
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/aktivity/${id}/hotovo`);
      setA((prev) => ({ ...prev, ...data }));
      onChanged?.();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const smazat = async () => {
    if (!window.confirm("Opravdu smazat tuto aktivitu?")) return;
    setBusy(true);
    try {
      await api.delete(`/api/aktivity/${id}`);
      onChanged?.();
      onClose();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const done = a?.stav === "HOTOVA";
  const overdue = a?.po_terminu;
  const canEdit = a?.muze_editovat !== false;
  const stav = done
    ? { label: "Hotovo", dot: "bg-success", pill: "bg-success-bg text-success" }
    : overdue
      ? { label: "Po termínu", dot: "bg-danger", pill: "bg-danger/10 text-danger" }
      : { label: "Otevřeno", dot: "bg-[#c9ced5]", pill: "bg-inputbg text-sec" };

  // Poznámky z provedení — ukládají se tlačítkem, ne při každém úhozu.
  const [zaznam, setZaznam] = useState("");
  const [stavZaznamu, setStavZaznamu] = useState(null);
  useEffect(() => { if (a) setZaznam(a.zaznam || ""); }, [a?.id]); // eslint-disable-line
  const zaznamZmenen = !!a && zaznam !== (a.zaznam || "");
  const ulozitZaznam = async () => {
    setStavZaznamu("nacita"); setErr("");
    try {
      await api.patch(`/api/aktivity/${id}`, { zaznam });
      setA((prev) => ({ ...prev, zaznam }));
      setStavZaznamu("hotovo"); setTimeout(() => setStavZaznamu(null), 1800);
      onChanged?.();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); setStavZaznamu(null); }
  };
  // Po dokončení rovnou naplánovat další krok (stejný klient, případ, lidé).
  const [navazujici, setNavazujici] = useState(false);
  // další pracovní den (o víkendu pondělí)
  const zitra = () => { const d = new Date(); do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6); return d; };

  // Zavření s odjezdem karty — onClose až po animaci.
  const [odjizdi, setOdjizdi] = useState(false);
  const zavrit = () => { if (odjizdi) return; setOdjizdi(true); setTimeout(() => onClose(), 220); };
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !navazujici) zavrit(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  });

  const TypIkona = ikonaTypu(a?.typ);
  const jit = (cesta) => { onClose(); nav(cesta); };   // přechod jinam bez odjezdu
  const kulate = "tap md:min-h-0 md:min-w-0 h-10 w-10 md:h-9 md:w-9 rounded-full bg-inputbg hover:bg-line-soft inline-flex items-center justify-center";
  // Úkol zálohy se pozná podle kroku; starší úkoly bez kroku podle názvu. Fakturu firma vystaví
  // ve svém účetním programu — tlačítko vede tam, kde je částka k vystavení a kam se nahraje.
  const fakturacni = a?.obchodni_pripad_id && !done && (
    a.krok === "ZALOHA" || a.uzavreni === "KONECNA_FAKTURA" || /(zálohovou|konečnou) fakturu/i.test(a.nazev || ""));

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-stretch justify-center md:justify-end md:p-3"
      data-testid="aktivita-detail">
      <div className={`absolute inset-0 bg-black/20 ${odjizdi ? "zaclon-out" : "zaclon-in"}`} onClick={zavrit} />
      <div className={`karta-panel ${odjizdi ? "odjizdi" : ""} relative bg-white w-full md:w-[440px] rounded-t-sheet md:rounded-[20px] shadow-[0_18px_50px_rgba(20,22,26,.22)] max-h-[92vh] md:max-h-none md:h-full overflow-y-auto`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} role="dialog" aria-modal="true">

        {/* horní lišta: smazat vlevo (daleko od zavření), upravit a zavřít vpravo */}
        <div className="flex items-center gap-2 px-3.5 pt-3.5 sticky top-0 bg-white z-10">
          {a && canEdit && (
            <button onClick={smazat} disabled={busy} className={`${kulate} text-muted hover:text-danger`} aria-label="Smazat" title="Smazat"
              data-testid="aktivita-detail-smazat"><Trash2 size={15} /></button>
          )}
          <span className="flex-1" />
          {a && canEdit && (
            <button onClick={() => onEdit?.(a)} disabled={busy} className={kulate} aria-label="Upravit" title="Upravit"
              data-testid="aktivita-detail-upravit"><Pencil size={15} /></button>
          )}
          <button onClick={zavrit} className={kulate} aria-label="Zavřít" data-testid="aktivita-detail-close"><X size={18} /></button>
        </div>

        {a === undefined && <div className="p-10 flex justify-center"><Spinner /></div>}
        {a === null && <div className="p-6 text-[13px] text-danger">{err || "Aktivita nenalezena."}</div>}

        {a && (
          <div className="px-5 pb-6">
            <div className="flex flex-col items-center text-center gap-2 pt-1.5 pb-5">
              <span className={`h-[60px] w-[60px] rounded-full inline-flex items-center justify-center transition-colors duration-300 ${
                done ? "bg-success text-white" : "bg-accent/15 text-[#a8410c]"}`} data-testid="aktivita-stav-ikona">
                {done ? <Check size={28} strokeWidth={2.8} className="vjezd" /> : <TypIkona size={26} strokeWidth={1.8} />}
              </span>
              <span className="t-meta font-600 text-muted">{a.typ} · {a.oblast === "SERVIS" ? "Servis" : "Obchod"}</span>
              <h2 className="t-nadpis text-ink" data-testid="aktivita-detail-nazev">{a.nazev}</h2>
              <span className="amount t-meta text-sec" data-testid="aktivita-detail-termin">{formatRozsah(a.date_start, a.date_end)}</span>
              {(done || overdue) && (
                <span className={`h-[26px] px-3 rounded-pill text-[12px] font-700 inline-flex items-center ${stav.pill}`} data-testid="aktivita-stav">
                  {stav.label}
                </span>
              )}
            </div>

            {!canEdit ? (
              <p className="text-center t-meta text-muted">Na úpravy této aktivity nemáš oprávnění.</p>
            ) : done ? (
              <div className="rise flex flex-col items-center gap-1.5" data-testid="aktivita-co-dal">
                {/* Hotovo → hned nabídnout další krok (stejný klient, případ i lidé). */}
                <button onClick={() => setNavazujici(true)} data-testid="aktivita-navazujici"
                  className="w-full h-[50px] rounded-pill bg-accent text-white text-[15px] font-700 inline-flex items-center justify-center gap-2 hover:bg-accent-hover active:scale-[.98] transition">
                  <Plus size={18} strokeWidth={2.6} /> Naplánovat další aktivitu
                </button>
                <button onClick={toggle} disabled={busy} data-testid="aktivita-detail-hotovo"
                  className="tap md:min-h-0 h-9 px-3 rounded-pill t-meta font-600 text-sec hover:text-ink inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Undo2 size={14} /> Vrátit mezi otevřené
                </button>
              </div>
            ) : (
              <button onClick={toggle} disabled={busy} data-testid="aktivita-detail-hotovo"
                className="w-full h-[50px] rounded-pill text-[15px] font-700 inline-flex items-center justify-center gap-2 bg-ink text-white hover:bg-black active:scale-[.98] transition disabled:opacity-60">
                <Check size={18} strokeWidth={2.6} /> Označit hotové
              </button>
            )}
            {fakturacni && (
              <button onClick={() => jit(`/obchod/${a.obchodni_pripad_id}?zalozka=ekonomika`)} data-testid="aktivita-vystavit-zalohu"
                className="mt-2 w-full h-[46px] rounded-pill bg-accent text-white t-telo font-700 inline-flex items-center justify-center gap-2 hover:bg-accent-hover">
                <Receipt size={16} /> Kolik vyfakturovat a nahrát fakturu
              </button>
            )}

            <div className="mt-5 bg-inputbg rounded-[16px] p-1">
              {a.osoby?.length > 0 && (
                <Radek ikona={UserRound} label={a.osoby.length > 1 ? "Odpovídají" : "Odpovídá"} testid="aktivita-detail-osoby">
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {a.osoby.map((o) => (
                      <span key={o.id} className="inline-flex items-center gap-1.5">
                        <OsobaKolecko id={o.id} name={o.name} url={o.photo_url} size={22} />{o.name}
                      </span>
                    ))}
                  </span>
                </Radek>
              )}
              {!a.osoby?.length && (
                <Radek ikona={UserRound} label="Odpovídá" testid="aktivita-detail-osoby">
                  <span className="text-muted font-500">nikdo — přiřaď v úpravě</span>
                </Radek>
              )}
              {a.upozornit_min >= 0 && !done && (
                <Radek ikona={Bell} label="Upozornění" testid="aktivita-detail-upozorneni">
                  {textUpozorneni(a.upozornit_min)}{a.upozornit_emailem ? " · i e-mailem" : ""}
                </Radek>
              )}
              {/* Co se dělá — technik musí hned vidět technologii, výrobce a typ zařízení. */}
              {(a.vybaveni || a.zarizeni_nazev || a.oblast === "SERVIS") && (
                <Radek ikona={Wrench} label="Zařízení" testid="aktivita-detail-zarizeni"
                  onClick={a.zarizeni_id ? () => jit(`/servis/zarizeni/${a.zarizeni_id}`) : undefined}>
                  <span className={`block ${a.vybaveni || a.zarizeni_nazev ? "" : "text-muted font-500"}`}>
                    {a.vybaveni?.technologie || a.zarizeni_nazev || "Zatím nepřiřazené"}
                  </span>
                  {!(a.vybaveni || a.zarizeni_nazev) ? null : a.vybaveni && (a.vybaveni.vyrobce || a.vybaveni.typ) ? (
                    <span className="block t-meta font-500 text-sec">
                      {[a.vybaveni.vyrobce, a.vybaveni.typ].filter(Boolean).join(" · ")}
                      {a.vybaveni.vyrobni_cislo && <span className="text-muted"> · v. č. {a.vybaveni.vyrobni_cislo}</span>}
                    </span>
                  ) : (
                    <span className="block t-meta font-500 text-muted">Výrobce a typ zatím nevyplněné</span>
                  )}
                </Radek>
              )}
              {a.client_name && (
                <Radek ikona={Home} label="Klient" onClick={() => jit(`/klienti/${a.client_id}`)} testid="aktivita-detail-klient">
                  {a.client_name}
                </Radek>
              )}
              {a.obchodni_pripad && (
                <Radek ikona={Briefcase} label="Obchodní případ" onClick={() => jit(`/obchod/${a.obchodni_pripad.id}`)}
                  testid="aktivita-detail-pripad">{a.obchodni_pripad.nazev}</Radek>
              )}

            </div>

            {a.poznamky && (
              <div className="mt-4 px-1" data-testid="aktivita-detail-poznamky">
                <div className="label-up text-muted mb-1">Popis</div>
                <div className="t-telo leading-relaxed text-sec whitespace-pre-wrap">{a.poznamky}</div>
              </div>
            )}

            {(canEdit || a.zaznam) && (
              <div className="mt-4" data-testid="aktivita-detail-zaznam">
                <label htmlFor="aktivita-zaznam" className="label-up text-muted block mb-1.5 px-1">Poznámky</label>
                <textarea id="aktivita-zaznam" rows={3} value={zaznam} readOnly={!canEdit}
                  onChange={(e) => setZaznam(e.target.value)} placeholder="Co se domluvilo, co je potřeba dál…"
                  className="field resize-y !h-auto py-2.5" data-testid="aktivita-zaznam" />
                {(zaznamZmenen || stavZaznamu) && (
                  <div className="rise mt-2 flex justify-end gap-2">
                    {zaznamZmenen && !stavZaznamu && (
                      <button onClick={() => setZaznam(a.zaznam || "")} className="btn-ghost !h-9">Zrušit</button>
                    )}
                    <Button className="!h-9" onClick={ulozitZaznam} disabled={!zaznamZmenen || stavZaznamu === "nacita"}
                      stav={stavZaznamu || undefined} data-testid="aktivita-zaznam-ulozit">Uložit poznámky</Button>
                  </div>
                )}
              </div>
            )}

            <Komentare aktivitaId={a.id} />

            {err && <div className="mt-3 text-[12.5px] text-danger">{err}</div>}
          </div>
        )}
      </div>
      {navazujici && a && (
        <NovaAktivita onClose={() => setNavazujici(false)}
          onSaved={() => { setNavazujici(false); onChanged?.(); onClose(); }}
          defaultClientId={a.client_id || null} defaultPripadId={a.obchodni_pripad_id || null}
          defaultOblast={a.oblast} defaultOsoby={a.odpovedne_osoby || null} defaultDate={zitra()} />
      )}
    </div>
  );
}
